/*
 * Pure helpers behind the Amazon offer columns and the Offers page.
 *
 * These are OUR counts from the sites' Snowplow events (an offer at least half
 * in view, once per page load; a click on any of its Amazon links). Amazon's
 * own clicks, orders and revenue come from the Associates reports and will
 * not match exactly.
 */
import { metricSlug } from './pageMetrics';

const EMPTY = { impressions: 0, clicks: 0, ctr: null, products: [] };

/* A page with no row had no offer seen in the window: a real zero. */
export const offersForPage = (index, page) => {
  if (!page) return EMPTY;
  const key = metricSlug(page.slug || page.id || page._id) || 'home';
  return (index && index[key]) || EMPTY;
};

/* The sites payload is keyed by domain id, which is the Snowplow app_id. */
export const offersForDomain = (payload, domainId) =>
  (payload && payload.domains && payload.domains[domainId]) || null;

/* CTR as a percentage; "—" when nothing was seen, never a misleading 0%. */
export const formatCtr = (ctr) => {
  if (ctr == null) return '—';
  const pct = Number(ctr) * 100;
  return `${pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
};

/* Sparse {day: n} -> dense array along the response's day_keys. */
export const dailySeries = (daily, dayKeys) =>
  (dayKeys || []).map((d) => Number((daily || {})[d] || 0));

/* Product label: library or page title, else the ASIN itself. */
export const productLabel = (p) => (p && (p.title || p.asin)) || 'unknown';

/* An Amazon product page for an ASIN; short links and "unknown" have none. */
export const amazonUrlFor = (asin) =>
  /^[A-Z0-9]{10}$/i.test(String(asin || '')) ? `https://www.amazon.com/dp/${asin}` : null;

/* Sort rows by a column: numbers descending (nulls last, ties by clicks then
   impressions), text A-Z; 'title' sorts by the label shown. */
export const sortOfferRows = (rows, key) =>
  [...(rows || [])].sort((a, b) => {
    if (key === 'title') return productLabel(a).localeCompare(productLabel(b));
    const av = a[key], bv = b[key];
    if (typeof av === 'string' || typeof bv === 'string') {
      return String(av || '').localeCompare(String(bv || ''));
    }
    if (av == null && bv != null) return 1;
    if (bv == null && av != null) return -1;
    return (bv || 0) - (av || 0) || (b.clicks - a.clicks) || (b.impressions - a.impressions);
  });
